// Master Apps · barreira de erro de render (React 19, classe com
// getDerivedStateFromError/componentDidCatch, sem dependência externa).
//
// Sem isso, qualquer exceção durante o render (um campo nulo inesperado num app
// antigo, por exemplo) desmonta a árvore inteira e o usuário fica com a tela
// branca, sem mensagem e sem saída. Montada em dois níveis:
//   1. main.tsx, por fora de tudo, como último recurso;
//   2. app.tsx, uma por rota (com key na location), para o erro de uma tela não
//      derrubar o app e o usuário conseguir navegar para outra.
//
// O fallback é propositalmente simples e SEM chrome (nada de Sidebar): se a
// exceção veio justamente do chrome ou de um provider, um fallback rico
// quebraria de novo. A navegação de escape é um link comum para /catalog.
import { Component, type ErrorInfo, type ReactNode } from 'react';
import { IconAlert, IconGrid, IconRefresh } from '@/components/icons';
import { byViewport, useViewport } from '@/hooks/use-viewport';
import { fontMono, fontSans, makePalette } from '@/lib/theme';
import { useScreenProps } from '@/store/theme';

interface ErrorBoundaryProps {
  children: ReactNode;
  /** Contexto no log do console, por exemplo "app" ou "rota /catalog". */
  label?: string;
}

interface ErrorBoundaryState {
  error: Error | null;
  componentStack: string | null;
}

function ErrorBoundaryFallback({
  error,
  componentStack,
  label,
}: Readonly<{
  error: Error;
  componentStack: string | null;
  label?: string;
}>) {
  const sp = useScreenProps();
  const p = makePalette(sp.dark);
  const vp = useViewport();

  // No toque os dois botões viram blocos de largura cheia e 46px de altura: em
  // 320px eles já quebravam de linha (o `flexWrap` abaixo), e dois retângulos de
  // 36px meio vazios eram mais difíceis de acertar do que um alvo por linha.
  const box = {
    height: byViewport(vp, { mobile: 46, desktop: 36 }),
    flex: byViewport(vp, { mobile: '1 1 100%', desktop: undefined }),
    justifyContent: 'center',
  } as const;

  const primaryStyle = {
    ...box,
    padding: '0 16px',
    background: sp.accent,
    color: '#fff',
    border: 'none',
    borderRadius: 8,
    fontSize: 12.5,
    fontWeight: 600,
    fontFamily: fontSans,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 7,
    textDecoration: 'none',
  } as const;

  const ghostStyle = {
    ...box,
    padding: '0 14px',
    background: 'transparent',
    color: p.textMuted,
    border: `1px solid ${p.borderStrong}`,
    borderRadius: 8,
    fontSize: 12.5,
    fontWeight: 600,
    fontFamily: fontSans,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 7,
    textDecoration: 'none',
  } as const;

  return (
    <div
      style={{
        width: '100%',
        // `100dvh` no compacto (a barra de endereço do celular come parte de
        // `100vh`); segue `minHeight`, então cartão alto continua rolando.
        // `--app-vh` de `globals.css`: cobre o `dvh` do celular COM fallback.
        minHeight: 'var(--app-vh)',
        display: 'flex',
        // Centro vertical só quando sobra tela: com `center` e conteúdo mais
        // alto que a viewport o flex corta o TOPO do cartão (e o topo é onde
        // está a mensagem).
        alignItems: byViewport(vp, { mobile: 'flex-start', desktop: 'center' }),
        justifyContent: 'center',
        padding: byViewport(vp, { mobile: 14, desktop: 24 }),
        background: p.bg,
        color: p.text,
        fontFamily: fontSans,
      }}
    >
      <div
        style={{
          width: '100%',
          // `min(480px, 100%)` em vez de 480 fixo: o cartão nunca passa da
          // largura útil, mesmo se um dia entrar num container sem padding.
          maxWidth: 'min(480px, 100%)',
          background: p.surface,
          border: `1px solid ${p.border}`,
          borderRadius: byViewport(vp, { mobile: 12, desktop: 14 }),
          padding: byViewport(vp, { mobile: 20, desktop: 30 }),
          boxShadow: sp.dark ? '0 18px 48px rgba(0,0,0,0.45)' : '0 18px 48px rgba(0,0,0,0.08)',
        }}
      >
        <div
          style={{
            width: byViewport(vp, { mobile: 40, desktop: 46 }),
            height: byViewport(vp, { mobile: 40, desktop: 46 }),
            borderRadius: 10,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: sp.dark ? '#dc262622' : '#fee2e2',
            color: sp.dark ? '#fca5a5' : '#b91c1c',
            marginBottom: byViewport(vp, { mobile: 12, desktop: 16 }),
          }}
        >
          <IconAlert size={byViewport(vp, { mobile: 19, desktop: 22 })} />
        </div>
        <div
          style={{
            // 18px em 320px quebrava "Algo quebrou nesta tela" em três linhas.
            fontSize: byViewport(vp, { mobile: 16.5, desktop: 18 }),
            fontWeight: 600,
            letterSpacing: '-0.018em',
            marginBottom: 8,
          }}
        >
          Algo quebrou nesta tela
        </div>
        <div style={{ fontSize: 13, color: p.muted, lineHeight: 1.65, marginBottom: 20 }}>
          O Master Apps encontrou um erro inesperado ao montar esta tela. Nenhum dado foi perdido no
          servidor: recarregue a página para continuar. Se o erro voltar, mande um print desta tela
          para a TI com o detalhe técnico abaixo.
        </div>

        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 18 }}>
          <button type="button" onClick={() => window.location.reload()} style={primaryStyle}>
            <IconRefresh size={13} />
            Recarregar a página
          </button>
          {/* Link comum (navegação dura): reseta a árvore inteira mesmo quando a
              exceção veio de fora do Router. */}
          <a href="/catalog" style={ghostStyle}>
            <IconGrid size={13} />
            Ir para o catálogo
          </a>
        </div>

        <details
          style={{
            background: p.surface2,
            border: `1px solid ${p.border}`,
            borderRadius: 10,
            padding: byViewport(vp, { mobile: '10px 10px', desktop: '10px 12px' }),
          }}
        >
          <summary
            style={{
              fontSize: 12,
              fontWeight: 600,
              color: p.textMuted,
              cursor: 'pointer',
              userSelect: 'none',
            }}
          >
            Detalhe técnico
          </summary>
          <pre
            style={{
              margin: '10px 0 0',
              fontFamily: fontMono,
              fontSize: byViewport(vp, { mobile: 11, desktop: 10.5 }),
              lineHeight: 1.55,
              color: p.muted,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              // A pilha aberta não pode virar a tela toda no celular — 150px já
              // mostram o erro e o resto rola dentro do <pre>.
              maxHeight: byViewport(vp, { mobile: 150, desktop: 220 }),
              overflow: 'auto',
            }}
          >
            {[
              label ? `contexto: ${label}` : null,
              `${error.name}: ${error.message}`,
              componentStack?.trim() || null,
            ]
              .filter(Boolean)
              .join('\n\n')}
          </pre>
        </details>
      </div>
    </div>
  );
}

export class ErrorBoundary extends Component<Readonly<ErrorBoundaryProps>, ErrorBoundaryState> {
  override state: ErrorBoundaryState = { error: null, componentStack: null };

  static getDerivedStateFromError(error: unknown): Partial<ErrorBoundaryState> {
    return { error: error instanceof Error ? error : new Error(String(error)) };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    this.setState({ componentStack: info.componentStack ?? null });
    // O contexto sai da interpolação para não aninhar template dentro de
    // template — o prefixo do log continua igual (com espaço antes do rótulo).
    const context = this.props.label ? ` ${this.props.label}` : '';
    // console é o único canal de log disponível no browser.
    console.error(`[ErrorBoundary]${context} erro de render:`, error, info.componentStack);
  }

  override render(): ReactNode {
    const { error, componentStack } = this.state;
    if (error) {
      return (
        <ErrorBoundaryFallback
          error={error}
          componentStack={componentStack}
          label={this.props.label}
        />
      );
    }
    return this.props.children;
  }
}
